import { useEffect, useRef } from "react";
import { loadGoogleIdentityServices } from "../../utils/googleIdentity";
import { GOOGLE_OAUTH_STATE_KEY, GOOGLE_OAUTH_RETURN_KEY, buildGoogleAuthUrl } from "../../utils/googleAuthRedirect";
import { translations } from "../../utils/i18n";

const GOOGLE_CLIENT_ID = "1029161680658-jrfh5teteeqgb6q8m4vjlavgh668tcir.apps.googleusercontent.com";

// Chrome (desktop + Android) supports the native FedCM sheet, so GIS's
// popup-based button works there. Browsers without it — notably every
// browser on iOS, since they all run on WebKit — fall back to a popup GIS
// opens itself, and that popup routinely fails to close after completing
// sign-in (a WebKit restriction: script can't close a window it didn't
// literally open with window.open), stranding the user on
// accounts.google.com/gsi/transform even though the credential already
// came through. For those browsers we skip GIS's button entirely and drive
// a plain top-level OAuth redirect instead, which has no popup to get
// stuck — see GoogleAuthCallbackPage for the return leg.
function supportsFedCM() {
  return typeof window !== "undefined" && "IdentityCredential" in window;
}

export default function GoogleSignInButton({ onCredential, locale = "en" }) {
  const buttonRef = useRef(null);
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;
  const fedCM = supportsFedCM();

  useEffect(() => {
    if (!fedCM) return;
    let cancelled = false;

    loadGoogleIdentityServices()
      .then((google) => {
        if (cancelled || !buttonRef.current) return;
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          ux_mode: "popup",
          use_fedcm_for_button: true,
          callback: (response) => onCredentialRef.current(response.credential),
        });
        google.accounts.id.renderButton(buttonRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          shape: "rectangular",
          text: "continue_with",
          logo_alignment: "left",
          locale,
          width: buttonRef.current.offsetWidth || 300,
        });
      })
      .catch(() => {
        // Silently leave the overlay empty — email/password sign-in still works.
      });

    return () => {
      cancelled = true;
    };
  }, [locale, fedCM]);

  function startRedirect() {
    const state = randomState();
    sessionStorage.setItem(GOOGLE_OAUTH_STATE_KEY, state);
    sessionStorage.setItem(GOOGLE_OAUTH_RETURN_KEY, JSON.stringify({ language: locale, returnPath: window.location.pathname }));
    window.location.href = buildGoogleAuthUrl({ clientId: GOOGLE_CLIENT_ID, state, locale });
  }

  if (!fedCM) {
    const label = translations[locale]?.common.continueWithGoogle ?? translations.en.common.continueWithGoogle;
    return (
      <div className="google-signin-button">
        <button type="button" className="google-signin-fallback" onClick={startRedirect}>
          <GoogleGlyph />
          <span>{label}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="google-signin-button">
      <div ref={buttonRef} className="google-signin-native" />
    </div>
  );
}

function randomState() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
